/**
 * GamificationContext - Global state for gamification on web
 *
 * Manages:
 * - Current user's XP and level
 * - Real-time subscriptions for XP/badge updates
 * - Celebration queue (level-ups, badges, streaks)
 * - Sound effect preferences
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { getVerifiedSession } from '@/lib/sessionGuard';
import { useAuth } from '@/contexts/AuthContext';
import LevelingService from '@bricksOS/valets/logic/LevelingService';
import { GamificationEvents } from '@/lots/gamification/services/XPService.js';

const GamificationContext = createContext(null);

export const CELEBRATION_TYPES = {
  LEVEL_UP: 'level_up',
  BADGE_EARNED: 'badge_earned',
  XP_GAINED: 'xp_gained',
  STREAK_MILESTONE: 'streak_milestone',
};

export const GamificationProvider = ({ children }) => {
  const { user, valetProfile } = useAuth();

  // Current user's gamification state
  const [totalXP, setTotalXP] = useState(0);
  const [level, setLevel] = useState(1);
  const [levelInfo, setLevelInfo] = useState({ level: 1, title: '🚗 Rookie', tier: 'rookie' });

  // Celebration queue
  const [celebrationQueue, setCelebrationQueue] = useState([]);
  const [currentCelebration, setCurrentCelebration] = useState(null);
  const [celebrationVisible, setCelebrationVisible] = useState(false);

  // XP gain indicator state
  const [recentXPGains, setRecentXPGains] = useState([]);

  // Settings
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const stored = localStorage.getItem('gamification_sound_enabled');
    return stored !== 'false'; // Default to enabled
  });

  /**
   * Load initial gamification data for current user
   */
  const loadUserGamificationData = useCallback(async () => {
    if (!valetProfile?.id) return;

    try {
      // Signed-in read: skip it rather than send the anon key (anon-valets review 2026-09-30).
      if (!(await getVerifiedSession(supabase.auth, user?.id))) return;

      const { data: valet, error } = await supabase
        .from('valets')
        .select('total_xp, level')
        .eq('id', valetProfile.id)
        .single();

      if (error) {
        console.error('[GamificationContext] Error loading valet data:', error);
        return;
      }

      const xp = valet?.total_xp || 0;
      setTotalXP(xp);

      const newLevelInfo = LevelingService.calculateLevel(xp);
      setLevel(newLevelInfo.level);
      setLevelInfo(newLevelInfo);

      console.log(`[GamificationContext] Loaded: ${xp} XP, Level ${newLevelInfo.level} (${newLevelInfo.title})`);
    } catch (error) {
      console.error('[GamificationContext] Error loading gamification data:', error);
    }
  }, [valetProfile?.id, user?.id]);

  /**
   * Add XP gain indicator to the list
   */
  const addXPGainIndicator = useCallback((amount, position = null) => {
    const id = `xp-${Date.now()}-${Math.random()}`;
    const gain = {
      id,
      amount,
      position: position || { x: Math.random() * 100 + 50, y: Math.random() * 50 + 100 },
      timestamp: Date.now(),
    };

    setRecentXPGains(prev => [...prev, gain]);

    // Remove after animation
    setTimeout(() => {
      setRecentXPGains(prev => prev.filter(g => g.id !== id));
    }, 2000);
  }, []);

  /**
   * Queue a celebration
   */
  const queueCelebration = useCallback((type, data) => {
    const celebration = {
      id: `${type}-${Date.now()}`,
      type,
      data,
      timestamp: Date.now(),
    };

    setCelebrationQueue(prev => [...prev, celebration]);
    console.log(`[GamificationContext] Queued celebration: ${type}`, data);
  }, []);

  /**
   * Process celebration queue - show next celebration
   */
  useEffect(() => {
    if (!celebrationVisible && celebrationQueue.length > 0) {
      const next = celebrationQueue[0];
      setCurrentCelebration(next);
      setCelebrationVisible(true);
      setCelebrationQueue(prev => prev.slice(1));
    }
  }, [celebrationVisible, celebrationQueue]);

  /**
   * Dismiss current celebration
   */
  const dismissCelebration = useCallback(() => {
    setCelebrationVisible(false);
    setTimeout(() => {
      setCurrentCelebration(null);
    }, 300);
  }, []);

  /**
   * Trigger level-up celebration (can be called manually or via events)
   */
  const celebrateLevelUp = useCallback((previousLevel, newLevel, levelTitle, xp) => {
    queueCelebration(CELEBRATION_TYPES.LEVEL_UP, {
      previousLevel,
      newLevel,
      levelTitle,
      totalXP: xp,
    });
  }, [queueCelebration]);

  /**
   * Trigger badge earned celebration
   */
  const celebrateBadgeEarned = useCallback((badge) => {
    queueCelebration(CELEBRATION_TYPES.BADGE_EARNED, {
      badge,
      badgeType: badge.slug,
    });
  }, [queueCelebration]);

  /**
   * Toggle sound effects
   */
  const toggleSound = useCallback(() => {
    setSoundEnabled(prev => {
      const newValue = !prev;
      localStorage.setItem('gamification_sound_enabled', String(newValue));
      return newValue;
    });
  }, []);

  // Subscribe to GamificationEvents from XPService
  useEffect(() => {
    const unsubLevelUp = GamificationEvents.subscribe('levelUp', (data) => {
      console.log('[GamificationContext] Received levelUp event from XPService', data);
      celebrateLevelUp(data.previousLevel, data.newLevel, data.levelTitle, data.totalXP);
    });

    const unsubBadge = GamificationEvents.subscribe('badgeEarned', (data) => {
      console.log('[GamificationContext] Received badgeEarned event from XPService', data);
      celebrateBadgeEarned({
        slug: data.badgeType,
        name: data.badgeName,
        category: data.category,
        xp_award: data.xpAwarded,
        rarity: data.rarity,
      });
    });

    const unsubXP = GamificationEvents.subscribe('xpGained', (data) => {
      if (data.amount > 0) {
        addXPGainIndicator(data.amount);
      }
    });

    return () => {
      unsubLevelUp?.();
      unsubBadge?.();
      unsubXP?.();
    };
  }, [celebrateLevelUp, celebrateBadgeEarned, addXPGainIndicator]);

  // Load data on mount/user change. No realtime channel: `valets` and `badge_assignments`
  // are NOT in supabase_realtime (both are readable by every login, anon included, and
  // `valets` takes millions of updates), so the channel that sat here never fired. Live XP
  // and badge moments arrive through GamificationEvents above (db-touch audit 2026-10-11).
  useEffect(() => {
    loadUserGamificationData();
  }, [loadUserGamificationData]);

  // Persist sound preference
  useEffect(() => {
    localStorage.setItem('gamification_sound_enabled', String(soundEnabled));
  }, [soundEnabled]);

  const value = {
    // State
    totalXP,
    level,
    levelInfo,
    currentCelebration,
    celebrationVisible,
    celebrationQueueLength: celebrationQueue.length,
    recentXPGains,
    soundEnabled,

    // Actions
    celebrateLevelUp,
    celebrateBadgeEarned,
    queueCelebration,
    dismissCelebration,
    addXPGainIndicator,
    toggleSound,
    refreshData: loadUserGamificationData,
  };

  return (
    <GamificationContext.Provider value={value}>
      {children}
    </GamificationContext.Provider>
  );
};

export const useGamification = () => {
  const context = useContext(GamificationContext);
  if (!context) {
    // Return a safe fallback instead of throwing
    // This allows components to use the hook outside the provider
    return {
      totalXP: 0,
      level: 1,
      levelInfo: { level: 1, title: '🚗 Rookie', tier: 'rookie' },
      currentCelebration: null,
      celebrationVisible: false,
      celebrationQueueLength: 0,
      recentXPGains: [],
      soundEnabled: true,
      celebrateLevelUp: () => {},
      celebrateBadgeEarned: () => {},
      queueCelebration: () => {},
      dismissCelebration: () => {},
      addXPGainIndicator: () => {},
      toggleSound: () => {},
      refreshData: () => {},
    };
  }
  return context;
};

export default GamificationContext;
